/**
 * Client configuration — URL params, server URLs, camera and render tuning.
 *
 * What is NOT here: physics. v1 kept its movement constants in this file and
 * ran them client-side, which is exactly why v1 accepted a self-reported
 * position. All movement tuning now lives in `@chaos/sim`'s `PHYSICS`, and the
 * client reaches it only by running the same `stepWorld` the server runs.
 */

export const urlParams = new URLSearchParams(window.location.search);

const isHttps = window.location.protocol === 'https:';
const wsProtocol = isHttps ? 'wss:' : 'ws:';

/**
 * STANDALONE-DEV FALLBACK ONLY. In the real flow the shell hands over
 * `realtimeUrl` in the `chaos:session` message and `Connection.join` uses that.
 *
 * Dev runs Vite on 4030 and the backend on `env.PORT` (default 3000) — HTTP and
 * WebSocket share one port there, so this is the backend's port, not a separate
 * Colyseus one. Prod is same-origin.
 */
export const isLocalhost =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

/**
 * Where the Colyseus socket goes.
 *
 * The `window.location.host` fallback assumes the client is served by the same
 * process as the API. That holds for a same-origin deploy and is WRONG whenever
 * the bundle is served separately — on the VPS it is static files behind its own
 * nginx on one port while the backend listens on another, so the fallback would
 * open a websocket against the file server. `VITE_SERVER_URL` is baked in at
 * build time for exactly that case.
 */
const configuredServer = import.meta.env?.VITE_SERVER_URL?.trim();

export const SERVER_URL =
  urlParams.get('server') ||
  (configuredServer
    ? configuredServer.replace(/^http/, 'ws').replace(/\/$/, '')
    : isLocalhost
      ? 'ws://localhost:3000'
      : `${wsProtocol}//${window.location.host}`);

/** Colyseus room name — must match `gameServer.define("arena", ArenaRoom)`. */
export const ROOM_NAME = 'arena';

/**
 * `?room=NAME` turns the gym from a sandbox into a shared arena.
 *
 * Its presence is the whole switch: with it the gym builds a `ScrimConnection`
 * and joins a real server room, without it a `LocalConnection` and the sim runs
 * in this tab. The name is the meeting point — two people who type the same one
 * land in the same fight, which is what `filterBy(["roomId"])` enforces server
 * side.
 */
export const scrimRoom = (urlParams.get('room') || '').trim().slice(0, 32);

/**
 * The session token is NOT read here.
 *
 * It arrives by postMessage from the shell — see `net/shellSession.js`. A token
 * in the query string goes everywhere the URL goes (history, referrer headers,
 * screenshots, shared links), which is why apps/web hands it over out of band.
 * The URL fallback in shellSession.js exists only for standalone dev.
 */

export function readMatchParams() {
  return {
    matchId: urlParams.get('match') || '',
    lobbyId: urlParams.get('lobby') || '',
  };
}

/**
 * The pot, in wei, as a decimal string.
 *
 * It comes from the lobby (apps/web), which is where the buy-in was priced and
 * where the entrants were counted; the room's Schema carries no pot, and the
 * `result` message only arrives at settlement. The HUD and the death-cam ticker
 * need it for the whole match, so the launcher passes it through.
 *
 * Kept as a STRING the entire way to the DOM. Parsing it into a Number would
 * lose precision above 2^53 wei, which is 0.009 ETH.
 */
export function readPotWei() {
  const raw = urlParams.get('pot');
  if (!raw) return '0';
  return /^\d+$/.test(raw) ? raw : '0';
}

/** Where a one-tap requeue sends the player. Owned by apps/web. */
export const LOBBY_URL = urlParams.get('lobbyUrl') || '/';

export const isDebug = urlParams.get('debug') === 'true';
export const forceWebGL = urlParams.get('forceWebGL') === 'true';

/**
 * Is the PRIMARY input a finger?
 *
 * This used to include `window.innerWidth < 820`, which is a proxy for "phone"
 * that a resized desktop window satisfies. That mattered more after the pivot
 * than before it: `isTouch` makes `initDesktopEvents` return early, and on the
 * fixed camera that is the only thing that installs mouse aim — so narrowing a
 * desktop window past 820px did not just swap the controls, it left the player
 * with no way to aim at all.
 *
 * `pointer: coarse` is the question actually being asked, and it is asked of
 * the primary pointer. Both conditions together give the right answer in the
 * three cases that matter:
 *
 *   phone            touch capable + coarse  -> touch controls
 *   desktop, narrow  not touch capable       -> mouse aim, ability row
 *   touch laptop     touch capable + FINE    -> mouse aim, and the ability row
 *                                              is real buttons it can still tap
 */
const hasTouchPoints = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;
export const isTouch = hasTouchPoints && coarsePointer;

// ── Camera ────────────────────────────────────────────────────────────────
// Pitch and zoom RANGE are v1's. The default distance is not — see below. An
// earlier pass pushed it out to v1's 20 because a closer camera "read as
// claustrophobic"; that finding was about how much ARENA you can see, and it is
// real. It trades against a different one, measured since: at 20 the character
// is 6.5% of screen height and a 2.6-unit melee cone is very hard to judge.
// The value below sits between the two rather than pretending either is wrong.
/*
 * MIN_PITCH / MAX_PITCH / MIN_DISTANCE / MAX_DISTANCE / DEFAULT_DISTANCE are
 * gone. They configured an orbit camera that no longer exists — the fixed
 * camera derives everything from TOPDOWN_PITCH/YAW/DISTANCE in
 * CameraController. Leaving them here left five knobs that look live and turn
 * nothing, which is worse than no knobs.
 */
/** Height above the player's centre the camera aims at. */
export const CAMERA_LOOK_HEIGHT = 1.0;
/** Degrees added to the base FOV at full sprint — the felt walk/sprint contrast. */
export const SPRINT_FOV_BOOST = 5;

/** Free-camera fly speeds, used by the death cam. */
export const FREE_FLY_SPEED = 26;
export const FREE_FAST_SPEED = 55;

// ── Netcode ───────────────────────────────────────────────────────────────
/** Input frames are batched this often. One flush carries 1-3 frames at 30Hz. */
export const INPUT_FLUSH_MS = 50;
/** How many unacked frames we keep for replay before assuming the link is gone. */
export const MAX_PENDING_INPUTS = 180;
/** Positional divergence (units) tolerated before we snap and replay. */
export const RECONCILE_EPSILON = 0.06;
/** A divergence this large is a teleport, not drift — snap hard, no smoothing. */
export const RECONCILE_HARD_SNAP = 2.5;
/** Ticks the client is willing to catch up in one frame after a stall. */
export const MAX_CATCHUP_TICKS = 6;

export const JOYSTICK_RADIUS = 56;
/** Below this stick magnitude we send zero — a resting thumb must not walk. */
export const JOYSTICK_DEADZONE = 0.14;
